import { Modal, Switch, Tooltip } from 'antd';
import userApi from 'api/userApi';
import { useAppDispatch, useAppSelector, useAppTranslation } from 'app/hooks';
import CloseIcon from 'assets/icons/CloseIcon';
import PasteIcon from 'assets/icons/PasteIcon';
import { setIsShowMfaModal } from 'features/home/homeSlice';
import useThemes from 'hooks/useThemes';
import useUserDetail from 'hooks/useUserDetail';
import { useNavigate } from 'react-router-dom';
import { selectCurrentUser } from 'redux/userSlice';
import { formatMoney } from 'utils/formatAmount';
import { formatDate } from 'utils/dateUtils';
import { platformStates } from 'redux/platformSlice';
import { useEffect, useState } from 'react';
import { useHandleGetWallets } from 'hooks/useHandleGetWallets';
import { showConfirmModal, showSuccessModal } from 'components/Modals/CommonModals';
import WarningIcon from 'assets/icons/WarningIcon';
import ChangeNamePage from 'features/auth/pages/ChangeNamePage';
import { FaCheck } from 'react-icons/fa';
import { FaEye } from 'react-icons/fa';
import UserReferralModal from './UserReferralModal';
import { FaPencil } from 'react-icons/fa6';
import ReferralCodeModal from './ReferralCodeModal';
interface UserProfileModalProps {
  visible: boolean;
  closeModal: () => void;
  title: string;
  onShowChangePasswordPage: () => void;
}

const getKYCStatus = (status: string | null | undefined): string => {
  switch (status) {
    case 'INPROGRESS':
      return 'userProfile.rowInfo.status.inprogress';
    case 'PENDING':
      return 'userProfile.rowInfo.status.pending';
    case 'APPROVED':
      return 'userProfile.rowInfo.status.approved';
    case 'RETRY':
      return 'userProfile.rowInfo.status.retry';
    case 'REJECTED':
      return 'userProfile.rowInfo.status.rejected';
    default:
      return 'userProfile.rowInfo.status.none';
  }
};

const getKYCStatusColor = (status: string | null | undefined): string => {
  switch (status) {
    case 'INPROGRESS':
    case 'PENDING':
      return 'text-orange';
    case 'APPROVED':
      return 'text-green';
    case 'RETRY':
    case 'REJECTED':
      return 'text-red';
    default:
      return 'text-gray-2';
  }
};

const UserProfileModal: React.FunctionComponent<UserProfileModalProps> = ({
  visible,
  closeModal,
  onShowChangePasswordPage,
  title,
}) => {
  const { currentTheme } = useThemes();
  const currentUser = useAppSelector(selectCurrentUser);
  const t = useAppTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { getUserDetail, getUser } = useUserDetail();
  const { isUserLoggedIn } = useUserDetail();

  const { userWallets } = useHandleGetWallets();
  const [coinBet, setCoinBet] = useState<number | null>(null);
  const [isShowChangeNameForm, setIsShowChangeNameForm] = useState<boolean>(false);
  const [isShowReferralModal, setIsShowReferralModal] = useState<boolean>(false);
  const [isOpenReferralCodeModal, setIsOpenReferralCodeModal] = useState<boolean>(false);

  const handleMfaClick = () => {
    dispatch(setIsShowMfaModal(true));
  };

  const redirectKYC = () => {
    navigate('/kyc');
  };

  const handleEnableStatus = async () => {
    const { ok } = await userApi.enableShareRanking();
    if (ok) {
      getUser();
    }
  };

  useEffect(() => {
    getUserDetail();
  }, []);

  useEffect(() => {
    if (currentUser?.coinDto) {
      setCoinBet(currentUser?.coinDto.id);
    }
    return currentUser?.coinDto && setCoinBet(currentUser?.coinDto.id);
  }, [currentUser?.coinDto.id]);

  const promotionExpiredTooltip = currentUser?.wallet && (
    <div>
      {currentUser?.wallet.promotionBalanceExpiresIn24hrs.map(
        (balance) =>
          balance.point > 0 && (
            <>
              <span className="row-infor__right span text-white" style={{ fontSize: '10px' }}>
                {`${formatMoney(balance.point || 0, 0)} ${t(
                  'userProfile.rowInfo.promotionBalanceExpired',
                )} ${formatDate(balance.expiredDate)}`}
              </span>
              <br />
            </>
          ),
      )}
    </div>
  );

  const changeBetWallet = async () => {
    if (coinBet) {
      const { ok } = await userApi.changeWallet(coinBet);

      if (ok) {
        getUser();
        showSuccessModal({
          title: t('bet-wallet.success'),
          content: '',
        });
      }
    }
  };

  const onCloseModal = () => {
    closeModal();
    currentUser?.coinDto && setCoinBet(currentUser?.coinDto.id);
  };

  const handleChangeBetting = () => {
    if (coinBet && coinBet !== currentUser?.coinDto.id) {
      const betWallet = userWallets.find((w) => w.coin.id === coinBet);
      const onCancel = () => {
        currentUser?.coinDto && setCoinBet(currentUser?.coinDto.id);
      };
      showConfirmModal({
        title: t('bet-wallet.confirm.title'),
        content: <p>{t('bet-wallet.confirm.message', { symbol: betWallet?.coin.symbol })}</p>,
        width: 360,
        icon: <WarningIcon />,
        onOk: () => changeBetWallet(),
        okText: t('button.ok'),
        cancelText: t('popup.buttons.cancel'),
        onCancel,
      });
    }
  };

  const handle2fa = (value: boolean) => {
    if (
      !currentUser?.kyc ||
      currentUser.kyc.status === 'INPROGRESS' ||
      currentUser.kyc.status === 'RETRY'
    ) {
      showConfirmModal({
        title: t('popup.kyc.title.required'),
        content: <p>{t('popup.kyc.message.kycRequired')}</p>,
        width: 360,
        icon: <WarningIcon />,
        onOk: () => {
          navigate('/kyc');
        },
        okText: t('popup.buttons.ok'),
        cancelText: t('popup.buttons.cancel'),
      });
      return;
    }
    dispatch(setIsShowMfaModal(true));
  };

  const handleReferralCodeClick = () => {
    setIsOpenReferralCodeModal((prev) => !prev);
  };

  useEffect(() => {
    return () => {
      setIsOpenReferralCodeModal(false);
      setIsShowReferralModal(false);
      setIsShowChangeNameForm(false);
    };
  }, [isUserLoggedIn]);

  const renderUserName = (firstName: string, lastName: string) => {
    if (`${firstName}${lastName}` === '') {
      return '--';
    }
    return `${firstName} ${lastName}`;
  };

  return (
    <Modal
      className="common-modal user-profile-modal"
      title={title}
      footer={null}
      visible={visible}
      centered
      maskClosable={false}
      closeIcon={<CloseIcon isDarkMode={currentTheme === 'dark'} width="20" height="20" />}
      width={600}
      onCancel={onCloseModal}
    >
      <div className="row-infor">
        <span className="row-infor__left span">{`${t('userProfile.rowInfo.email')}:`}</span>
        <span className="row-infor__right span">{currentUser?.email || ''}</span>
      </div>
      <div className="row-infor">
        <span className="row-infor__left span">{`${t('userProfile.rowInfo.name')}:`}</span>
        <div className="row-infor__right">
          <span className=" span">
            {renderUserName(currentUser?.firstName || '', currentUser?.lastName || '')}
          </span>
          {/* <button
            className="button-change-name" 
            onClick={() => setIsShowChangeNameForm(true)}
          >
            Change
          </button> */}
        </div>
      </div>
      {/* <div className="row-infor">
        <span className="row-infor__left span">{`${t(
          'userProfile.rowInfo.promotionBalance',
        )}:`}</span>
        <div className="flex-row items-center row-infor__right gap-2">
          <span className="span">
            {formatMoney(currentUser?.wallet?.promoBalance || 0, 0)} (USD)
          </span>
          {currentUser?.wallet &&
            currentUser.wallet.promotionBalanceExpiresIn24hrs &&
            currentUser.wallet.promotionBalanceExpiresIn24hrs.length > 0 && (
              <Tooltip placement={isPhone ? 'bottom' : 'rightTop'} title={promotionExpiredTooltip}>
                <Button
                  type="dashed"
                  shape="circle"
                  icon={<InfoCircleOutlined className="tooltip-icon" />}
                  className="tooltip-button"
                />
              </Tooltip>
            )}
        </div>
      </div> */}
      <div className="row-infor">
        <span className="row-infor__left span">{`${t('userProfile.rowInfo.kycStatus')}:`}</span>
        <div className="row-infor__right flex-row items-center gap-2">
          <span className={`span ${getKYCStatusColor(currentUser?.kyc?.status)}`}>{`${t(
            getKYCStatus(currentUser?.kyc?.status),
          )}`}</span>
          {(!currentUser?.kyc ||
            currentUser.kyc.status === 'RETRY' ||
            currentUser.kyc.status === 'INPROGRESS') && (
            <button className="button w-fit-content kyc" onClick={redirectKYC}>
              {t('userProfile.verify')}
            </button>
          )}
        </div>
      </div>
      {/* <div className="row-infor">
        <span className="row-infor__left span">{`${t('userProfile.rowInfo.shareRanking')}:`}</span>
        <div className="row-infor__right span">
          <div className="w-full flex justify-center items-center gap-3">
            <div className="relative w-full">
              <Switch
                defaultChecked={currentUser?.isShareRanking}
                className="ranking_check"
                onChange={handleEnableStatus}
              />
            </div>
          </div>
        </div>
      </div> */}
      {/* <div className="row-infor">
        <span className="row-infor__left span">{t('bet-wallet.title')}:</span>
        <div className="row-infor__right span ">
          <Select
            className="antd__select primary-text-color"
            dropdownClassName="antd__select-dropdown"
            value={coinBet}
            onChange={onCoinChange}
            style={{ width: 120 }}
          >
            {userWallets &&
              userWallets
                .filter((w) => w.isBet)
                .map((w) => (
                  <Select.Option value={w.coin?.id}>
                    <span>
                      <img src={w.coin.image} width={20} height={20} /> {w.coin.symbol}
                    </span>
                  </Select.Option>
                ))}
          </Select>
          {coinBet && coinBet !== currentUser?.coinDto.id && (
            <Button
              shape="circle"
              icon={<SaveOutlined />}
              size="small"
              className="share-bet__btn ml-3 main-button-background"
              onClick={handleChangeBetting}
            />
          )}
        </div>
      </div> */}

      <div className="row-infor">
        <span className="row-infor__left span"> {t('userProfile.rowInfo.changePassword')}:</span>
        <button className="row-infor__right span button" onClick={onShowChangePasswordPage}>
          Change
        </button>
      </div>
      <div className="row-infor">
        <span className="row-infor__left span">2FA:</span>
        <Switch
          checked={currentUser?.isEnableMfa}
          className="ranking_check"
          onChange={(value) => handle2fa(value)}
        />
      </div>
      <ChangeNamePage
        isShow={isShowChangeNameForm}
        closePage={() => setIsShowChangeNameForm(false)}
      />
      <UserReferralModal
        visible={isUserLoggedIn && isShowReferralModal}
        closeModal={() => setIsShowReferralModal(false)}
      />
    </Modal>
  );
};

export default UserProfileModal;
